import { addDays, addMonths, differenceInYears } from "date-fns";
import { z } from "zod";
import { ADDRESS_CATEGORIES } from "@/lib/constants";
import { getICColour } from "@/lib/utils";

export const customerInformationSchema = z
  .object({
    customerType: z.enum([
      "RES",
      "MOBI-POSTPAID",
      "FREEDOM",
      "MOBI-PREPAID",
      "TOURIST",
    ]),
    salutation: z.enum(["Mr", "Ms"]),
    gender: z.enum(["M", "F"]),
    name: z
      .string({ required_error: "Full Name is required" })
      .min(3, { message: "Full Name must be at least 3 characters" }),
    nricNumber: z
      .string()
      .min(1, { message: "IC Number / Passport Number is required" }),
    nricExpirationDate: z.date({
      required_error: "IC / Passport Expiration Date is required",
    }),
    dateOfArrival: z
      .date()
      .min(new Date(), {
        message: "Date of arrival must be after today",
      })
      .optional(),
    flightNumber: z.string().optional(),
    email: z.string().min(1, { message: "Email is required" }).email(),
    mobileNumber: z
      .string()
      .regex(/(^[78]\d{6}$)|(^22[89]\d{4}$)/, {
        message:
          "Please enter a valid Brunei mobile phone number (e.g. 7/8xxxxxx, 228xxxx, 229xxxx)",
      })
      .optional(),
    homeNumber: z.string().optional(),
    officeNumber: z.string().optional(),
    dateOfBirth: z
      .date({ required_error: "Date of birth is required" })
      .optional(),
    citizenship: z
      .enum(["Brunei Darussalam", "Permanent Resident", "Foreigner"])
      .optional(),
    country: z.string().optional(),
    fulfilmentType: z
      .enum(["Instant Delivery", "Delivery", "Pick Up"])
      .optional(),
    fulfilmentDate: z.date().optional(),
    store: z.string().optional(),
    deliveryAddress: z
      .object({
        unit: z.string().optional(),
        floor: z.string().optional(),
        buildingName: z.string().optional(),
        houseNumber: z.string().optional(),
        block: z.string().optional(),
        simpang: z.string().optional(),
        street: z.string().optional(),
        village: z.string().optional(),
        mukim: z.string().optional(),
        district: z.string().optional(),
        address: z.string().optional(),
        postalCode: z.string().optional(),
        customPostalCode: z.string().optional(),
        addressCategory: z.enum(ADDRESS_CATEGORIES).optional(),
      })
      .optional(),
    isSameAsDeliveryAddress: z.boolean().optional(),
    billingAddress: z
      .object({
        unit: z.string().optional(),
        floor: z.string().optional(),
        buildingName: z.string().optional(),
        houseNumber: z.string().optional(),
        block: z.string().optional(),
        simpang: z.string().optional(),
        street: z.string().optional(),
        village: z.string().optional(),
        mukim: z.string().optional(),
        district: z.string().optional(),
        address: z.string().optional(),
        postalCode: z.string().optional(),
        customPostalCode: z.string().optional(),
        addressCategory: z.enum(ADDRESS_CATEGORIES).optional(),
      })
      .optional(),
  })
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Part of validation
  .superRefine((data, context) => {
    const icColour = getICColour(data.nricNumber);

    if (
      (icColour === "green" || icColour === "passport") &&
      data.customerType !== "MOBI-PREPAID" &&
      data.nricExpirationDate < addMonths(new Date(), 3)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expiration not less than 3 months",
        path: ["nricExpirationDate"],
      });
    }

    if (data.customerType !== "TOURIST") {
      if (!data.mobileNumber) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Mobile phone is required",
          path: ["mobileNumber"],
        });
      }

      if (
        data.homeNumber &&
        (data.homeNumber.length < 7 || data.homeNumber.length > 10)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Landline number must be between 7 and 10 digits",
          path: ["homeNumber"],
        });
      }

      if (
        data.officeNumber &&
        (data.officeNumber.length < 7 || data.officeNumber.length > 10)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Landline number must be between 7 and 10 digits",
          path: ["officeNumber"],
        });
      }

      if (!data.dateOfBirth) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Date of birth is required",
          path: ["dateOfBirth"],
        });
      }

      if (data.dateOfBirth) {
        const age = differenceInYears(new Date(), data.dateOfBirth);
        const isForeigner = data.citizenship === "Foreigner";

        if (data.customerType === "MOBI-PREPAID" && age < 12) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Minimum age is 12 years old.",
            path: ["dateOfBirth"],
          });
        } else if (
          data.customerType === "MOBI-POSTPAID" &&
          !isForeigner &&
          age < 16
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Minimum age is 16 years old.",
            path: ["dateOfBirth"],
          });
        } else if (data.customerType !== "MOBI-PREPAID" && age < 18) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Minimum age is 18 years old.",
            path: ["dateOfBirth"],
          });
        }
      }

      if (!data.citizenship) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Citizenship is required",
          path: ["citizenship"],
        });
      }

      if (data.citizenship !== "Brunei Darussalam" && !data.country) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Country is required",
          path: ["country"],
        });
      }

      if (data.customerType !== "RES") {
        if (!data.fulfilmentType) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Fulfilment type is required",
            path: ["fulfilmentType"],
          });
        }

        if (data.fulfilmentDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (
            data.fulfilmentType === "Instant Delivery" &&
            data.fulfilmentDate < today
          ) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Delivery date must be today or later.",
              path: ["fulfilmentDate"],
            });
          }

          if (data.fulfilmentType === "Delivery") {
            const minDeliveryDate = addDays(today, 2);
            if (data.fulfilmentDate < minDeliveryDate) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Delivery date must be at least 2 days from today.",
                path: ["fulfilmentDate"],
              });
            }
          }

          if (data.fulfilmentType === "Pick Up") {
            const minPickupDate = addDays(today, 1);
            if (data.fulfilmentDate < minPickupDate) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Pick up date must be tomorrow or later.",
                path: ["fulfilmentDate"],
              });
            }
          }
        } else {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Fulfilment date is required",
            path: ["fulfilmentDate"],
          });
        }

        if (data.fulfilmentType === "Pick Up" && !data.store) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Store is required",
            path: ["store"],
          });
        }
      }

      if (
        data.customerType === "RES" ||
        ((data.customerType === "FREEDOM" ||
          data.customerType === "MOBI-POSTPAID" ||
          data.customerType === "MOBI-PREPAID") &&
          data.fulfilmentType === "Delivery")
      ) {
        if (
          !(
            data.deliveryAddress?.district &&
            data.deliveryAddress?.mukim &&
            data.deliveryAddress?.village
          )
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Delivery address is required",
            path: ["deliveryAddress.village"],
          });
        }

        if (!data.deliveryAddress?.postalCode) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Postal code is required",
            path: ["deliveryAddress.postalCode"],
          });
        }

        if (
          data.deliveryAddress?.postalCode === "Other" &&
          !data.deliveryAddress?.customPostalCode
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Postal code is required",
            path: ["deliveryAddress.customPostalCode"],
          });
        }

        if (!data.deliveryAddress?.street) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Street is required",
            path: ["deliveryAddress.street"],
          });
        }

        if (!data.deliveryAddress?.simpang) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Simpang is required",
            path: ["deliveryAddress.simpang"],
          });
        }

        if (!data.deliveryAddress?.houseNumber) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "House number is required",
            path: ["deliveryAddress.houseNumber"],
          });
        }
      }

      if (
        data.customerType !== "MOBI-PREPAID" &&
        data.isSameAsDeliveryAddress !== undefined &&
        !data.isSameAsDeliveryAddress
      ) {
        if (
          !(
            data.billingAddress?.district &&
            data.billingAddress?.mukim &&
            data.billingAddress?.village
          )
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Billing address is required",
            path: ["billingAddress.village"],
          });
        }

        if (!data.billingAddress?.postalCode) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Postal code is required",
            path: ["billingAddress.postalCode"],
          });
        }

        if (
          data.billingAddress?.postalCode === "Other" &&
          !data.billingAddress?.customPostalCode
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Postal code is required",
            path: ["billingAddress.customPostalCode"],
          });
        }

        if (!data.billingAddress?.street) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Street is required",
            path: ["billingAddress.street"],
          });
        }

        if (!data.billingAddress?.simpang) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Simpang is required",
            path: ["billingAddress.simpang"],
          });
        }

        if (!data.billingAddress?.houseNumber) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "House number is required",
            path: ["billingAddress.houseNumber"],
          });
        }
      }
    }

    if (data.customerType === "TOURIST") {
      if (!data.dateOfArrival) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Date of arrival is required",
          path: ["dateOfArrival"],
        });
      }

      if (!data.flightNumber) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Flight number is required",
          path: ["flightNumber"],
        });
      }

      if (!data.store) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Store is required",
          path: ["store"],
        });
      }
    }
  });
